import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Empty } from "antd";
import { formatDuration, respondentName, respondentEmail } from "./responseFormat";
import styles from "./style.module.scss";

const PREVIEW_TEXTS = 2;

const TextAnswers = ({ summary }) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const items = showAll ? summary.texts : summary.texts.slice(0, PREVIEW_TEXTS);
  return (
    <>
      {items.map(({ response, text }) => (
        <div key={response.id} className={styles.quote}>
          {text}
          <span className={styles.quoteBy}>
            {respondentName(response) || respondentEmail(response) || `#${response.id}`}
          </span>
        </div>
      ))}
      {summary.texts.length > PREVIEW_TEXTS ? (
        <button type="button" className={styles.linkBtnAccent} onClick={() => setShowAll((v) => !v)}>
          {showAll
            ? t("feedbackCapture.responsesPage.show_less")
            : t("feedbackCapture.responsesPage.show_all_answers", { count: summary.texts.length })}
        </button>
      ) : null}
    </>
  );
};

// Open-text cards take a full row. Choice cards sit two to a row; one without a partner next to it
// (the next or previous card is open text) takes the full row so no half row stays empty.
function isWide(summaries, index) {
  if (summaries[index].kind === "text") return true;
  let start = index;
  while (start > 0 && summaries[start - 1].kind === "choice") start -= 1;
  let end = index;
  while (end < summaries.length - 1 && summaries[end + 1].kind === "choice") end += 1;
  const runLength = end - start + 1;
  return runLength % 2 === 1 && index === end;
}

const ResponsesSummary = ({ counts, summaries }) => {
  const { t } = useTranslation();
  const average = formatDuration(counts.averageSeconds, t);

  if (!counts.completed) {
    return <Empty description={t("feedbackCapture.responses_empty")} />;
  }

  return (
    <div>
      <div className={styles.kpis}>
        <div className={styles.kpi}>
          <div className={styles.kpiValue}>{counts.completed}</div>
          <div className={styles.kpiLabel}>{t("feedbackCapture.responsesPage.kpi_completed")}</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiValue}>%{counts.completionRate}</div>
          <div className={styles.kpiLabel}>
            {t("feedbackCapture.responsesPage.kpi_completion_rate", {
              completed: counts.completed,
              total: counts.total,
            })}
          </div>
        </div>
        {average ? (
          <div className={styles.kpi}>
            <div className={styles.kpiValue}>{average}</div>
            <div className={styles.kpiLabel}>{t("feedbackCapture.responsesPage.kpi_average_time")}</div>
          </div>
        ) : null}
      </div>
      <div className={styles.summaryGrid}>
        {summaries.map((summary, index) => (
          <section
            key={summary.question.id}
            className={[styles.summaryCard, isWide(summaries, index) ? styles.summaryCardWide : ""]
              .filter(Boolean)
              .join(" ")}
          >
            <h3 className={styles.summaryTitle}>{summary.question.text || summary.question.id}</h3>
            <div className={styles.summarySub}>
              {t("feedbackCapture.responsesPage.answered_count", { count: summary.answered })}
              {summary.average != null
                ? ` · ${t("feedbackCapture.responsesPage.average_rating", { value: summary.average })}`
                : null}
            </div>
            {summary.kind === "choice"
              ? summary.options.map((option) => (
                  <div key={option.label} className={styles.bar}>
                    <div className={styles.barRow}>
                      <span>{option.label}</span>
                      <span>
                        {option.count} · %{option.percent}
                      </span>
                    </div>
                    <div className={styles.track}>
                      <div className={styles.fill} style={{ width: `${option.percent}%` }} />
                    </div>
                  </div>
                ))
              : null}
            {summary.kind === "text" ? <TextAnswers summary={summary} /> : null}
          </section>
        ))}
      </div>
    </div>
  );
};

export default ResponsesSummary;
